/**
 * CategoryRow - Individual category row with toggle switch (WP-CAT-11, WP-CAT-13)
 *
 * Displays a single category with its name, icon, system badge, and toggle.
 *
 * Features:
 * - Category icon display (SVG icons via CategoryIcon component)
 * - System category badge
 * - Toggle switch for enabling/disabling
 * - Truncation for long names with tooltip
 * - Indentation for child categories
 */
import { cn } from '@/utils/cn';
import ToggleSwitch from '@/components/ui/ToggleSwitch';
import Tooltip from '@/components/ui/Tooltip';
import { CategoryIcon } from '@/components/categories';
import type { CategoryWithPreference } from '@/types/category';

export interface CategoryRowProps {
  /** Category data */
  category: CategoryWithPreference;
  /** Callback when toggle is clicked */
  onToggle: (categoryId: string, isEnabled: boolean) => void;
  /** Whether the toggle is currently updating */
  isUpdating?: boolean;
  /** Depth level for indentation (0 = root, 1 = child, etc.) */
  depth?: number;
}

/** Maximum name length before truncation */
const MAX_NAME_LENGTH = 40;

/**
 * Individual category row component.
 *
 * Accessibility:
 * - Toggle linked to category name via aria-labelledby
 * - Tooltip shows full name on truncated names
 * - Depth communicated via aria-level
 */
export default function CategoryRow({
  category,
  onToggle,
  isUpdating = false,
  depth = 0,
}: CategoryRowProps) {
  const needsTruncation = category.name.length > MAX_NAME_LENGTH;
  const displayName = needsTruncation
    ? `${category.name.slice(0, MAX_NAME_LENGTH)}...`
    : category.name;

  // Calculate indentation based on depth (for nested categories)
  const indentStyle = depth > 0 ? { marginLeft: `${depth * 1.5}rem` } : undefined;

  return (
    <div
      className={cn(
        'flex items-center justify-between py-3 px-2 hover:bg-gray-50 rounded-md transition-colors',
        !category.isEnabled && 'opacity-60'
      )}
      style={indentStyle}
      role="listitem"
      aria-level={depth + 1}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Category icon */}
        <span className="flex-shrink-0" aria-hidden="true">
          <CategoryIcon
            name={category.icon}
            size="md"
            color={category.color}
            className="text-gray-600"
          />
        </span>

        {/* Category name */}
        <div className="flex items-center gap-2 min-w-0">
          {needsTruncation ? (
            <Tooltip content={category.name}>
              <span
                id={`category-${category.id}-label`}
                className="text-sm font-medium text-gray-900 truncate cursor-help"
              >
                {displayName}
              </span>
            </Tooltip>
          ) : (
            <span
              id={`category-${category.id}-label`}
              className="text-sm font-medium text-gray-900 truncate"
            >
              {displayName}
            </span>
          )}

          {/* System badge */}
          {category.isSystem && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 flex-shrink-0">
              System
            </span>
          )}
        </div>
      </div>

      {/* Toggle switch */}
      <div className="flex-shrink-0 ml-4">
        <ToggleSwitch
          id={`category-${category.id}-toggle`}
          label={`${category.isEnabled ? 'Disable' : 'Enable'} ${category.name}`}
          enabled={category.isEnabled}
          onChange={(enabled) => onToggle(category.id, enabled)}
          isLoading={isUpdating}
        />
      </div>
    </div>
  );
}
